import React, { useCallback, useMemo, useState } from 'react';
import type { CoverBankBackground } from '../../../redux/api/fymAPI.types';
import { config } from '../../../config/config';
import { TypedMemo } from '../../common/types';
import type { CoverEditorContextValue, CoverPublicTool } from '../CoverEditorContext';
import { CoverEditorShell } from '../CoverEditorShell';
import { useCoverEditorDoc } from '../doc/useCoverEditorDoc';
import { ContinueDialog } from './ContinueDialog';
import { coverSignUpHref } from './coverSignUp';
import { EmailDialog } from './EmailDialog';
import { fieldBoxText, PUBLIC_COVER_THEME, publicCoverData } from './publicCoverData';
import {
  browserCoverStorage,
  LOCAL_CARRY,
  readLeadEmail,
  readLeadToken,
  writeCarry,
  type PublicCover,
} from './publicCoverStore';
import { PublicNotices } from './PublicNotices';
import { PublicTopBar } from './PublicTopBar';
import { useCoverLeadDownload } from './useCoverLeadDownload';
import { usePublicCoverPersist } from './usePublicCoverPersist';
import type { PublicCoverNote } from './usePublicCoverSource';

interface PublicCoverEditorProps {
  initial: PublicCover;
  initialNote: PublicCoverNote;
  backgrounds: CoverBankBackground[];
  /** `?embed=1`: inside the frame of the website page. */
  isEmbed: boolean;
  isSignedIn: boolean;
  /** The text of the title and artist boxes while a field is empty. */
  placeholders: { title: string; artist: string };
}

/** Opens an address of the app: in the page around the frame when the tool is embedded. */
function openAppAddress(href: string, isEmbed: boolean): void {
  if (isEmbed && window.top !== null) {
    window.top.location.href = href;
  } else {
    window.location.href = href;
  }
}

/**
 * The public tool (owner decisions D11, D12, D7; design cover-editor g): the shared editor frame
 * with the data of a visitor: no account, no song, no versions, no AI. The design lives in this
 * browser; the two fields are the song. Download asks for the email once, then saves the 3000 px
 * JPEG with the watermark. Every way to an account keeps the design for the sign-in first.
 */
const PublicCoverEditorComponent: React.FC<PublicCoverEditorProps> = (props) => {
  const { initial, initialNote, backgrounds, isEmbed, isSignedIn, placeholders } = props;
  const editor = useCoverEditorDoc(initial.doc);
  const { state, dispatch } = editor;
  const [title, setTitle] = useState(initial.title);
  const [artist, setArtist] = useState(initial.artist);
  const [note, setNote] = useState(initialNote);
  usePublicCoverPersist({ doc: state.doc, title, artist });
  const download = useCoverLeadDownload({ doc: state.doc, title, artist, isSignedIn });

  // A field is the text of every box of its role; an empty field shows the placeholder on the cover.
  const setField = useCallback(
    (role: 'title' | 'artist', value: string) => {
      const text = fieldBoxText(value, placeholders[role]);
      state.doc.texts
        .filter((box) => box.role === role)
        .forEach((box) => dispatch({ type: 'updateText', id: box.id, patch: { text }, coalesce: `field:${role}` }));
    },
    [dispatch, placeholders, state.doc.texts]
  );
  const onTitle = useCallback(
    (value: string) => {
      setTitle(value);
      setField('title', value);
    },
    [setField]
  );
  const onArtist = useCallback(
    (value: string) => {
      setArtist(value);
      setField('artist', value);
    },
    [setField]
  );

  /** Keeps the design for the sign-in: the saved design of the lead, else the design of this browser. */
  const keepForSignIn = useCallback(() => {
    const storage = browserCoverStorage();
    writeCarry(storage, readLeadToken(storage) ?? LOCAL_CARRY);
  }, []);
  const signUp = useCallback(() => {
    keepForSignIn();
    openAppAddress(coverSignUpHref({ email: readLeadEmail(browserCoverStorage()) }), isEmbed);
  }, [isEmbed, keepForSignIn]);
  const publicTool = useMemo<CoverPublicTool>(
    () => ({ onSignUp: signUp, plansHref: `${config.smartLinksWebURL}pricing`, isEmbed }),
    [isEmbed, signUp]
  );
  const account = useMemo(
    () => (isSignedIn ? null : { logInHref: coverSignUpHref({ signIn: true }), onLogIn: keepForSignIn }),
    [isSignedIn, keepForSignIn]
  );

  const data = useMemo(() => publicCoverData({ title, artist, themeId: PUBLIC_COVER_THEME }), [artist, title]);
  const base = useMemo<Omit<CoverEditorContextValue, 'tool' | 'setTool'>>(
    () => ({
      mode: 'public',
      accountId: 0,
      catalogItemUuid: '',
      designUuid: '',
      data,
      backgrounds,
      state,
      dispatch,
      canUndo: editor.canUndo,
      canRedo: editor.canRedo,
      readOnly: false,
      publicTool,
    }),
    [backgrounds, data, dispatch, editor.canRedo, editor.canUndo, publicTool, state]
  );
  const dismissNote = useCallback(() => setNote(null), []);
  const renderTopBar = useCallback(
    () => (
      <PublicTopBar
        title={title}
        artist={artist}
        onTitle={onTitle}
        onArtist={onArtist}
        account={account}
        onDownload={download.start}
        isDownloading={download.isBusy && download.dialog === 'closed'}
      />
    ),
    [account, artist, download.dialog, download.isBusy, download.start, onArtist, onTitle, title]
  );

  return (
    <CoverEditorShell
      base={base}
      renderTopBar={renderTopBar}
      notices={
        <PublicNotices
          note={note}
          onDismiss={dismissNote}
          isFileFailed={download.isFileFailed}
          title={title}
          artist={artist}
          placeholders={placeholders}
        />
      }
      hasWatermark
    >
      <EmailDialog
        isOpen={download.dialog === 'email'}
        isBusy={download.isBusy}
        error={download.error}
        onSubmit={(email) => {
          download.submit(email).catch(console.error);
        }}
        onChange={download.clearError}
        onCancel={download.close}
      />
      <ContinueDialog
        isOpen={download.dialog === 'done' && !isSignedIn}
        sentTo={download.sentTo}
        onSignUp={signUp}
        onStay={download.close}
      />
    </CoverEditorShell>
  );
};

export const PublicCoverEditor = TypedMemo(PublicCoverEditorComponent);
